/**
 * @file custom-shipping-form-utils.ts
 *
 * Pure helpers for the custom-shipping (zone) form: building method-option ids,
 * seeding the editable fields from a loaded zone (parsing the WooCommerce method
 * settings into the camelCase form data), the empty method defaults, mapping the
 * form data back to WooCommerce settings (the flat-rate cost formula and the
 * free-shipping `requires`/`min_amount`/`ignore_discounts` keys), and diffing the
 * edited method list against its seed into add/update/delete operations. Kept
 * pure so the mapping and diff are unit-testable without rendering or the store.
 */

import type {
  ShippingMethod,
  ShippingZone,
  ShippingZoneLocation,
} from '../../api/shop-types';
import {
  hasQuantityFee,
  parseFlatRateBaseCost,
  parseFlatRateQuantityFee,
  shippingRequirementNeedsMinimumAmount,
} from '../shipping-method-utils';
import { FLAT_RATE, FREE_SHIPPING } from '../shipping-constants';
import { withAllRegionsFallback } from '../shipping-region-utils';
import type {
  CustomShippingFormFields,
  FlatRateData,
  FreeShippingData,
  MethodFormOption,
  ShippingMethodType,
} from './custom-shipping-form-types';

/** Counter for unsaved method-option ids (stable React keys within a session). */
let nextUnsavedMethodId = 0;

/**
 * Builds a stable id for a method option: `"<type>-<instanceId>"` for a saved
 * method, or `"<type>-new-<n>"` for an unsaved one.
 *
 * @param methodType - The method type.
 * @param instanceId - The WooCommerce instance id, when saved.
 *
 * @returns The option id.
 */
export function createMethodOptionId(
  methodType: ShippingMethodType,
  instanceId?: string,
): string {
  if (instanceId !== undefined) {
    return `${methodType}-${instanceId}`;
  }
  nextUnsavedMethodId += 1;
  return `${methodType}-new-${nextUnsavedMethodId}`;
}

/**
 * Whether a required amount input is invalid: empty, not a finite number, or
 * negative. Used by the flat-rate cost / per-item fee and the free-shipping
 * minimum-amount validators.
 *
 * @param value - The amount input.
 *
 * @returns `true` when the value is not a finite, non-negative number.
 */
export function isInvalidRequiredAmount(value: string): boolean {
  const num = Number(value);
  return value.trim() === '' || !Number.isFinite(num) || num < 0;
}

/** The default fields for a new flat-rate method. */
export function emptyFlatRateData(): FlatRateData {
  return {
    enabled: true,
    title: '',
    taxStatus: 'taxable',
    cost: '0',
    additionalFeeEnabled: false,
    additionalFeeCost: '0',
  };
}

/** The default fields for a new free-shipping method. */
export function emptyFreeShippingData(): FreeShippingData {
  return {
    enabled: true,
    title: '',
    requires: '',
    minAmount: '0',
    ignoreDiscounts: false,
  };
}

/** Reads a setting value, falling back to an empty string. */
function settingValue(method: ShippingMethod, key: string): string {
  return method.settings[key]?.value ?? '';
}

/** Parses a flat-rate method's settings into the form data. */
function flatRateDataFromMethod(method: ShippingMethod): FlatRateData {
  const cost = settingValue(method, 'cost');
  return {
    enabled: method.enabled,
    title: method.title || settingValue(method, 'title'),
    taxStatus: settingValue(method, 'tax_status') || 'taxable',
    cost: parseFlatRateBaseCost(cost),
    additionalFeeEnabled: hasQuantityFee(cost),
    additionalFeeCost: parseFlatRateQuantityFee(cost) ?? '0',
  };
}

/** Parses a free-shipping method's settings into the form data. */
function freeShippingDataFromMethod(method: ShippingMethod): FreeShippingData {
  return {
    enabled: method.enabled,
    title: method.title || settingValue(method, 'title'),
    requires: settingValue(method, 'requires'),
    minAmount: settingValue(method, 'min_amount') || '0',
    ignoreDiscounts: settingValue(method, 'ignore_discounts') === 'yes',
  };
}

/** Maps a loaded method to a form option, or `null` for an unsupported type. */
export function methodToFormOption(method: ShippingMethod): MethodFormOption | null {
  if (method.method_id === FLAT_RATE) {
    return {
      id: createMethodOptionId('flat_rate', method.instance_id),
      instanceId: method.instance_id,
      methodType: 'flat_rate',
      data: flatRateDataFromMethod(method),
    };
  }
  if (method.method_id === FREE_SHIPPING) {
    return {
      id: createMethodOptionId('free_shipping', method.instance_id),
      instanceId: method.instance_id,
      methodType: 'free_shipping',
      data: freeShippingDataFromMethod(method),
    };
  }
  return null;
}

/**
 * Seeds the editable fields from a loaded zone (or blank for create). The
 * locations fall back to the all-regions sentinel for display.
 *
 * @param zone - The zone, or `undefined` to create.
 * @param methods - The zone's methods.
 * @param locations - The zone's locations.
 *
 * @returns The initial {@link CustomShippingFormFields}.
 */
export function deriveCustomShippingFields(
  zone: ShippingZone | undefined,
  methods: ShippingMethod[],
  locations: ShippingZoneLocation[],
): CustomShippingFormFields {
  if (!zone) {
    return { name: '', locations: withAllRegionsFallback([]), methods: [] };
  }
  return {
    name: zone.name,
    locations: withAllRegionsFallback(locations),
    methods: methods
      .map(methodToFormOption)
      .filter((option): option is MethodFormOption => option !== null),
  };
}

/**
 * Builds the WooCommerce cost value for a flat-rate method, appending the
 * per-item quantity-fee term when enabled (`"base + (fee * [qty])"`).
 *
 * @param data - The flat-rate form data.
 *
 * @returns The cost string.
 */
export function getFlatRateCostValue(data: FlatRateData): string {
  if (!data.additionalFeeEnabled || data.additionalFeeCost === '') {
    return data.cost;
  }
  return `${data.cost} + (${data.additionalFeeCost} * [qty])`;
}

/** Maps flat-rate form data to the WooCommerce settings write shape. */
export function buildFlatRateSettings(data: FlatRateData): Record<string, string> {
  return {
    title: data.title,
    tax_status: data.taxStatus,
    cost: getFlatRateCostValue(data),
  };
}

/** Maps free-shipping form data to the WooCommerce settings write shape. */
export function buildFreeShippingSettings(
  data: FreeShippingData,
): Record<string, string> {
  return {
    title: data.title,
    requires: data.requires,
    ...(shippingRequirementNeedsMinimumAmount(data.requires)
      ? {
          min_amount: data.minAmount,
          ignore_discounts: data.ignoreDiscounts ? 'yes' : 'no',
        }
      : {}),
  };
}

/** Builds the settings for any method option (dispatching on its type). */
export function buildMethodSettings(option: MethodFormOption): Record<string, string> {
  return option.methodType === 'flat_rate'
    ? buildFlatRateSettings(option.data)
    : buildFreeShippingSettings(option.data);
}

/** A method create operation (POST then settings update). */
export interface MethodAddOp {
  methodType: ShippingMethodType;
  settings: Record<string, string>;
  enabled: boolean;
  /** The method's position in the edited list — persisted so the method order sticks. */
  sortOrder: number;
}

/** A method update operation. */
export interface MethodUpdateOp {
  instanceId: string;
  settings: Record<string, string>;
  enabled: boolean;
  /** The method's position in the edited list — persisted so the method order sticks. */
  sortOrder: number;
}

/** The set of method mutations to apply when saving an edited zone. */
export interface MethodOps {
  toDelete: string[];
  toAdd: MethodAddOp[];
  toUpdate: MethodUpdateOp[];
}

/**
 * Diffs the edited method list against its seed: methods present in the seed but
 * gone from the current list are deleted, methods carrying an `instanceId` are
 * updated, and methods without one are added.
 *
 * @param seed - The methods as loaded from the API (with instance ids).
 * @param current - The edited method list.
 *
 * @returns The add/update/delete operations.
 */
export function diffMethods(
  seed: MethodFormOption[],
  current: MethodFormOption[],
): MethodOps {
  const currentInstanceIds = new Set(
    current
      .map((option) => option.instanceId)
      .filter((id): id is string => id !== undefined),
  );
  const toDelete = seed
    .map((option) => option.instanceId)
    .filter((id): id is string => id !== undefined && !currentInstanceIds.has(id));

  const toAdd: MethodAddOp[] = [];
  const toUpdate: MethodUpdateOp[] = [];
  // The list index IS the desired order: carry it as sortOrder on every op so the saved
  // order matches what the user dragged (methods otherwise all default to sort_order 0).
  current.forEach((option, index) => {
    const settings = buildMethodSettings(option);
    if (option.instanceId !== undefined) {
      toUpdate.push({ instanceId: option.instanceId, settings, enabled: option.data.enabled, sortOrder: index });
    } else {
      toAdd.push({ methodType: option.methodType, settings, enabled: option.data.enabled, sortOrder: index });
    }
  });
  return { toDelete, toAdd, toUpdate };
}
